import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Edit } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import MensagensTab from "@/components/MensagensTab";
import { OrcamentoProdutosTab } from "@/components/OrcamentoProdutosTab";

interface Orcamento {
  id: string;
  nome: string;
  escopo: string;
  data_validade: string;
  status: string;
  created_at: string;
  projetos?: { nome: string; valor_maximo?: number };
  clientes?: { nome: string };
  empresas?: { razao_social: string };
  fornecedores?: { razao_social: string };
  locais?: { nome: string };
}

export default function OrcamentoView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [orcamento, setOrcamento] = useState<Orcamento | null>(null);
  const [loading, setLoading] = useState(true);

  const loadOrcamento = async () => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from("orcamentos")
        .select(`
          id,
          nome,
          escopo,
          data_validade,
          status,
          created_at,
          projetos(nome, valor_maximo),
          clientes(nome),
          empresas(razao_social),
          fornecedores(razao_social),
          locais(nome)
        `)
        .eq("id", id)
        .single();

      if (error) throw error;
      setOrcamento(data as any);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao carregar orçamento",
        variant: "destructive",
      });
      navigate("/intranet/propostas/orcamentos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrcamento();
  }, [id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("pt-BR");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!orcamento) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Orçamento não encontrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/intranet/propostas/orcamentos")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{orcamento.nome}</h1>
            <p className="text-muted-foreground">
              Visualização do orçamento
            </p>
          </div>
        </div>
        <Button onClick={() => navigate(`/intranet/propostas/orcamentos/${id}/edit`)}>
          <Edit className="h-4 w-4 mr-2" />
          Editar
        </Button>
      </div>

      <Tabs defaultValue="dados-basicos" className="space-y-6">
        <TabsList>
          <TabsTrigger value="dados-basicos">Dados Básicos</TabsTrigger>
          <TabsTrigger value="relacionamentos">Relacionamentos</TabsTrigger>
          <TabsTrigger value="produtos">Produtos</TabsTrigger>
          <TabsTrigger value="mensagens">Mensagens</TabsTrigger>
        </TabsList>

        <TabsContent value="dados-basicos">
          <Card>
            <CardHeader>
              <CardTitle>Dados Básicos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Nome</label>
                  <p className="text-base">{orcamento.nome}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Projeto</label>
                  <p className="text-base">{orcamento.projetos?.nome || "-"}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Data de Validade</label>
                  <p className="text-base">
                    {orcamento.data_validade ? formatDate(orcamento.data_validade) : "-"}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                    orcamento.status === "aprovado" ? "bg-green-100 text-green-800" :
                    orcamento.status === "pendente" ? "bg-yellow-100 text-yellow-800" :
                    orcamento.status === "rejeitado" ? "bg-red-100 text-red-800" :
                    "bg-gray-100 text-gray-800"
                  }`}>
                    {orcamento.status || "Rascunho"}
                  </span>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Data de Criação</label>
                  <p className="text-base">{formatDate(orcamento.created_at)}</p>
                </div>
              </div>

              {orcamento.escopo && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Escopo</label>
                  <p className="text-base whitespace-pre-wrap">{orcamento.escopo}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="relacionamentos">
          <Card>
            <CardHeader>
              <CardTitle>Relacionamentos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Cliente</label>
                  <p className="text-base">{orcamento.clientes?.nome || "-"}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Empresa</label>
                  <p className="text-base">{orcamento.empresas?.razao_social || "-"}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Fornecedor</label>
                  <p className="text-base">{orcamento.fornecedores?.razao_social || "-"}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Local</label>
                  <p className="text-base">{orcamento.locais?.nome || "-"}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="produtos">
          <OrcamentoProdutosTab orcamentoId={id!} projetoId={orcamento.projetos?.valor_maximo ? orcamento.id : undefined} readonly />
        </TabsContent>

        <TabsContent value="mensagens">
          <MensagensTab tela="orcamentos" idRegistro={id!} />
        </TabsContent>
      </Tabs>
    </div>
  );
}